import React from 'react'
import { Icon } from '../../../../../atoms/Icon'
import { InfoTooltip } from '../../../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../../../TooltipMetricExplanation'
import { Badge } from '../../../../../atoms/Badge'
import {
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricHighlight,
} from '../../../BuildingCharacteristics.styles'
import type { ConformityBreakdownCardProps } from '../NeighborhoodContextTab.types'

export const ConformityBreakdownCard: React.FC<
  ConformityBreakdownCardProps
> = ({ buildingConformity, t }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <Icon name="check-circle" size="small" />
          {t('neighborhood.cards.conformityBreakdown.title')}
          <InfoTooltip>
            <TooltipMetricExplanation
              title={t('neighborhood.cards.conformityBreakdown.tooltip.title')}
              shortDescription={t(
                'neighborhood.cards.conformityBreakdown.tooltip.description'
              )}
              calculation={t(
                'neighborhood.cards.conformityBreakdown.tooltip.calculation'
              )}
              importance={t(
                'neighborhood.cards.conformityBreakdown.tooltip.importance'
              )}
              source={t('neighborhood.conformity.tooltip.source')}
            />
          </InfoTooltip>
        </CardTitle>
        <Badge
          variant={
            buildingConformity.score >= 70
              ? 'success'
              : buildingConformity.score >= 40
                ? 'warning'
                : 'danger'
          }
        >
          {buildingConformity.score >= 70
            ? t('neighborhood.conformity.fitsWell')
            : buildingConformity.score >= 40
              ? t('neighborhood.conformity.partialFit')
              : t('neighborhood.conformity.outlier')}
        </Badge>
      </CardHeader>
      <MetricGrid>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.conformity.temporalMatch.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.conformity.temporalMatch.tooltip.title')}
                shortDescription={t(
                  'neighborhood.conformity.temporalMatch.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.conformity.temporalMatch.tooltip.calculation'
                )}
                source={t('neighborhood.conformity.temporalMatch.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricHighlight
            $type={
              buildingConformity.temporal >= 70
                ? 'success'
                : buildingConformity.temporal >= 40
                  ? 'warning'
                  : 'danger'
            }
            $textAlign="right"
          >
            {buildingConformity.temporal.toFixed(1)}
          </MetricHighlight>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.conformity.physicalMatch.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t('neighborhood.conformity.physicalMatch.tooltip.title')}
                shortDescription={t(
                  'neighborhood.conformity.physicalMatch.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.conformity.physicalMatch.tooltip.calculation'
                )}
                source={t('neighborhood.conformity.physicalMatch.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricHighlight
            $type={
              buildingConformity.physical >= 70
                ? 'success'
                : buildingConformity.physical >= 40
                  ? 'warning'
                  : 'danger'
            }
            $textAlign="right"
          >
            {buildingConformity.physical.toFixed(1)}
          </MetricHighlight>
        </MetricRow>
        <MetricRow>
          <MetricLabel>
            {t('neighborhood.conformity.typologicalMatch.label')}
            <InfoTooltip>
              <TooltipMetricExplanation
                title={t(
                  'neighborhood.conformity.typologicalMatch.tooltip.title'
                )}
                shortDescription={t(
                  'neighborhood.conformity.typologicalMatch.tooltip.description'
                )}
                calculation={t(
                  'neighborhood.conformity.typologicalMatch.tooltip.calculation'
                )}
                source={t('neighborhood.conformity.typologicalMatch.source')}
              />
            </InfoTooltip>
          </MetricLabel>
          <MetricHighlight
            $type={
              buildingConformity.typological >= 70
                ? 'success'
                : buildingConformity.typological >= 40
                  ? 'warning'
                  : 'danger'
            }
            $textAlign="right"
          >
            {buildingConformity.typological.toFixed(1)}
          </MetricHighlight>
        </MetricRow>
      </MetricGrid>
      {buildingConformity.score < 40 && (
        <div
          style={{
            padding: '12px 16px',
            marginTop: '12px',
            backgroundColor: '#f8d7da',
            borderLeft: '4px solid #dc3545',
            fontSize: '13px',
            color: '#721c24',
          }}
        >
          <strong>{t('neighborhood.notes.title')}</strong>{' '}
          {t('neighborhood.notes.differsByPattern')}
          {buildingConformity.temporal < 40 &&
            ` ${t('neighborhood.notes.temporalMismatch')}`}
          {buildingConformity.physical < 40 &&
            ` ${t('neighborhood.notes.physicalMismatch')}`}
          {buildingConformity.typological < 40 &&
            ` ${t('neighborhood.notes.typologicalMismatch')}`}
        </div>
      )}
    </Card>
  )
}
